import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useMemo, useEffect } from "react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  GripVertical,
  Combine,
  CheckCircle2,
  XCircle,
  MoreVertical,
  Check,
  X,
  AlertTriangle,
  ChevronDown,
  RefreshCw
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ICONES, ICONE_PADRAO } from "@/lib/icones";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { generateSlug } from "@/utils/slug";

export const Route = createFileRoute("/admin/segmentos")({
  component: SegmentosAdmin,
});

function SegmentosAdmin() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [editingSegmento, setEditingSegmento] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [mergeDestinoId, setMergeDestinoId] = useState<string>("");
  const [isSlugAuto, setIsSlugAuto] = useState(true);
  const [formData, setFormData] = useState({
    nome: "",
    slug: "",
    icone: "Package",
    ativo: true,
    ordem: 0
  });

  const { data: segmentos, isLoading } = useQuery({
    queryKey: ["admin-segmentos"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("segmentos")
        .select(`
          *,
          fornecedores:fornecedores(id)
        `)
        .order("ordem", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      // Validar ícone
      if (!ICONES[data.icone]) {
        throw new Error("Ícone inválido, escolha um da lista");
      }

      // Validar duplicidade de nome
      const nomeNormalizado = data.nome.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
      const duplicado = segmentos?.find(s => 
        s.id !== editingSegmento?.id && 
        s.nome.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim() === nomeNormalizado
      );

      if (duplicado) {
        throw new Error(`Já existe um segmento com o nome "${duplicado.nome}". Deseja mesclá-los?`);
      }

      // Validar duplicidade de slug
      const slugDuplicado = segmentos?.find(s => s.id !== editingSegmento?.id && s.slug === data.slug);
      if (slugDuplicado) {
        throw new Error(`O slug "${data.slug}" já está em uso pelo segmento "${slugDuplicado.nome}".`);
      }

      if (editingSegmento) {
        const { error } = await supabase
          .from("segmentos")
          .update(data)
          .eq("id", editingSegmento.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("segmentos")
          .insert(data);
        if (error) throw error;
      }
    },
    onError: (error: any) => {
      toast.error(error.message || "Erro ao salvar segmento.");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-segmentos"] });
      setIsModalOpen(false);
      setEditingSegmento(null);
      setFormData({ nome: "", slug: "", icone: "Store", ativo: true, ordem: 0 });
      setIsSlugAuto(true);
      toast.success("Segmento salvo com sucesso!");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("segmentos").delete().eq("id", id);
      if (error) throw error;
    },
    onError: (error: any) => {
      toast.error(error.message || "Erro ao excluir segmento.");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-segmentos"] });
      toast.success("Segmento excluído!");
    },
  });

  const mergeMutation = useMutation({
    mutationFn: async ({ origemIds, destinoId }: { origemIds: string[], destinoId: string }) => {
      const { error } = await supabase.rpc('mesclar_segmentos', {
        p_origem_ids: origemIds,
        p_destino_id: destinoId
      });
      if (error) throw error;
    },
    onError: (error: any) => {
      toast.error(error.message || "Erro ao mesclar segmentos.");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-segmentos"] });
      setIsMergeModalOpen(false);
      setSelectedIds([]);
      setMergeDestinoId("");
      toast.success("Segmentos mesclados com sucesso!");
    },
  });

  const batchStatusMutation = useMutation({
    mutationFn: async ({ ids, ativo }: { ids: string[], ativo: boolean }) => {
      const { data, error } = await supabase
        .from("segmentos")
        .update({ ativo })
        .in("id", ids)
        .select();

      if (error) {
        console.error("Erro ao atualizar status em lote:", error);
        throw error;
      }
      return { count: data?.length || 0 };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin-segmentos"] });
      setSelectedIds([]);
      if (data.count === 0) {
        toast.warning("Nenhum segmento foi alterado. Verifique as permissões.");
      } else {
        toast.success(`${data.count} segmento(s) atualizado(s) em lote!`);
      }
    },
    onError: (error: any) => {
      toast.error(`Erro: ${error.message || "Falha ao atualizar segmentos"}`);
    }
  });

  const fixSlugsMutation = useMutation({
    mutationFn: async () => {
      const { data: segments, error } = await supabase
        .from("segmentos")
        .select("id, nome, slug");

      if (error) throw error;

      let count = 0;
      for (const segment of segments) {
        const correctSlug = generateSlug(segment.nome);
        if (segment.slug !== correctSlug) {
          const { error: updateError } = await supabase
            .from("segmentos")
            .update({ slug: correctSlug })
            .eq("id", segment.id);
          if (updateError) throw updateError;
          count++;
        }
      }
      return count;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["admin-segmentos"] });
      toast.success(`${count} slugs corrigidos com sucesso!`);
    },
    onError: (error: any) => {
      toast.error(error.message || "Erro ao corrigir slugs.");
    }
  });

  const handleEdit = (seg: any) => {
    setEditingSegmento(seg);
    setFormData({
      nome: seg.nome,
      slug: seg.slug,
      icone: seg.icone,
      ativo: seg.ativo,
      ordem: seg.ordem || 0
    });
    setIsSlugAuto(seg.slug === generateSlug(seg.nome));
    setIsModalOpen(true);
  };

  const handleDelete = (seg: any) => {
    if (seg.fornecedores?.length > 0) {
      toast.error(`Este segmento tem ${seg.fornecedores.length} fornecedores vinculados.`);
      return;
    }
    if (confirm("Tem certeza que deseja excluir este segmento?")) {
      deleteMutation.mutate(seg.id);
    }
  };

  const IconComponent = ({ name, className }: { name: string, className?: string }) => {
    const Icon = ICONES[name] ?? ICONE_PADRAO;
    return <Icon className={className} />;
  };

  const SUGGESTED_ICONS = Object.keys(ICONES);

  const ICON_SYNONYMS: Record<string, string[]> = {
    "Shirt": ["roupa", "vestuario", "moda", "camiseta"],
    "Footprints": ["sapato", "calcado", "tenis", "pe"],
    "Package": ["caixa", "entrega", "pacote", "estoque"],
    "Gem": ["joia", "acessorio", "luxo", "brilhante"],
    "Smartphone": ["celular", "telefone", "mobile", "tecnologia"],
    "Car": ["carro", "veiculo", "automovel", "transporte"],
    "Home": ["casa", "lar", "decoracao", "imovel"],
    "UtensilsCrossed": ["comida", "restaurante", "talher", "gastronomia"],
    "FileText": ["papel", "documento", "arquivo", "escritorio"],
    "PawPrint": ["cachorro", "pet", "animal", "gato"],
    "ShoppingBag": ["sacola", "compras", "varejo"],
    "Baby": ["bebe", "infantil", "crianca"],
    "Watch": ["relogio", "tempo", "acessorio"],
    "Hammer": ["ferramenta", "obra", "construcao"],
    "Wrench": ["ferramenta", "manutencao", "oficina"],
    "Coins": ["dinheiro", "moeda", "financas"],
    "Truck": ["frete", "entrega", "logistica"],
    "Store": ["loja", "comercio", "estabelecimento"]
  };

  const IconPicker = ({ value, onChange }: { value: string, onChange: (val: string) => void }) => {
    const [iconSearch, setIconSearch] = useState("");
    const [showAdvanced, setShowAdvanced] = useState(false);

    const allIconNames = Object.keys(ICONES);

    const filteredIcons = useMemo(() => {
      const search = iconSearch.toLowerCase();
      if (!search) return SUGGESTED_ICONS;

      return allIconNames.filter(name => {
        const matchesName = name.toLowerCase().includes(search);
        const synonyms = ICON_SYNONYMS[name] || [];
        const matchesSynonym = synonyms.some(s => s.includes(search));
        return matchesName || matchesSynonym;
      }).slice(0, 48); // Limit for performance
    }, [iconSearch]);

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-between h-12 px-3 border-2 border-dashed hover:border-primary/50 transition-all">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                <IconComponent name={value} className="w-5 h-5" />
              </div>
              <span className="font-medium text-gray-700">{value}</span>
            </div>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input 
                placeholder="Buscar ícone (ex: roupa, sapato...)" 
                className="pl-9 h-9"
                value={iconSearch}
                onChange={(e) => setIconSearch(e.target.value)}
              />
            </div>
          </div>
          <ScrollArea className="h-[300px]">
            <div className="p-3">
              {!iconSearch && (
                <>
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">Sugeridos</h4>
                  <div className="grid grid-cols-6 gap-2 mb-4">
                    {SUGGESTED_ICONS.map(name => (
                      <button
                        key={name}
                        onClick={() => onChange(name)}
                        className={cn(
                          "flex flex-col items-center justify-center p-2 rounded-lg hover:bg-primary/10 transition-colors group",
                          value === name && "ring-2 ring-[#C6F764] bg-primary/5"
                        )}
                        title={name}
                      >
                        <IconComponent name={name} className="w-5 h-5 text-gray-600 group-hover:text-primary" />
                        <span className="text-[8px] text-gray-400 mt-1 truncate w-full text-center">{name}</span>
                      </button>
                    ))}
                  </div>
                  <Separator className="my-3" />
                </>
              )}
              
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">
                {iconSearch ? `Resultados para "${iconSearch}"` : "Todos os ícones"}
              </h4>
              <div className="grid grid-cols-6 gap-2">
                {filteredIcons.map(name => (
                  <button
                    key={name}
                    onClick={() => onChange(name)}
                    className={cn(
                      "flex flex-col items-center justify-center p-2 rounded-lg hover:bg-primary/10 transition-colors group",
                      value === name && "ring-2 ring-[#C6F764] bg-primary/5"
                    )}
                    title={name}
                  >
                    <IconComponent name={name} className="w-5 h-5 text-gray-600 group-hover:text-primary" />
                    <span className="text-[8px] text-gray-400 mt-1 truncate w-full text-center">{name}</span>
                  </button>
                ))}
              </div>
            </div>
          </ScrollArea>
          <div className="p-2 border-t bg-gray-50/50">
            <button 
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-[10px] text-gray-500 hover:text-primary flex items-center gap-1 mx-auto py-1"
            >
              {showAdvanced ? "Ocultar avançado" : "Configuração avançada (nome exato)"}
            </button>
            {showAdvanced && (
              <div className="p-2 pt-0">
                <Input 
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  className="h-8 text-xs"
                  placeholder="Nome do Lucide Icon"
                />
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  useEffect(() => {
    if (!editingSegmento && formData.nome) {
      const nome = formData.nome.toLowerCase();
      let sugerido = "";
      
      if (nome.includes("calcado") || nome.includes("sapato") || nome.includes("pe")) sugerido = "Footprints";
      else if (nome.includes("roupa") || nome.includes("vestuario") || nome.includes("moda") || nome.includes("camiseta")) sugerido = "Shirt";
      else if (nome.includes("bijuteria") || nome.includes("joia") || nome.includes("acessorio")) sugerido = "Gem";
      else if (nome.includes("eletronico") || nome.includes("celular") || nome.includes("tecnologia")) sugerido = "Smartphone";
      else if (nome.includes("alimento") || nome.includes("bebida") || nome.includes("comida")) sugerido = "UtensilsCrossed";
      else if (nome.includes("pet") || nome.includes("animal")) sugerido = "PawPrint";
      else if (nome.includes("embalagem") || nome.includes("materiais")) sugerido = "Package";
      else if (nome.includes("oculos")) sugerido = "Glasses";
      else if (nome.includes("relogio")) sugerido = "Watch";
      else if (nome.includes("tecido") || nome.includes("costura")) sugerido = "Scissors";
      else if (nome.includes("construcao") || nome.includes("ferramenta")) sugerido = "Wrench";
      else if (nome.includes("games") || nome.includes("brinquedo")) sugerido = "Gamepad2";
      else if (nome.includes("esporte")) sugerido = "Bike";
      else if (nome.includes("perfume") || nome.includes("aroma")) sugerido = "SprayCan";
      else if (nome.includes("noiva") || nome.includes("casamento")) sugerido = "Crown";
      else if (nome.includes("papelaria") || nome.includes("festa")) sugerido = "PartyPopper";
      
      if (sugerido && ICONES[sugerido]) {
        setFormData(prev => ({ ...prev, icone: sugerido }));
      }
    }
  }, [formData.nome, editingSegmento]);

  const filteredSegmentos = segmentos?.filter(s => 
    s.nome.toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredSegmentos?.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredSegmentos?.map(s => s.id) || []);
    }
  };

  const handleBatchDelete = async () => {
    const segmentsWithSuppliers = segmentos?.filter(s => selectedIds.includes(s.id) && s.fornecedores?.length > 0);
    if (segmentsWithSuppliers && segmentsWithSuppliers.length > 0) {
      toast.error(`Os seguintes segmentos têm fornecedores e não podem ser excluídos: ${segmentsWithSuppliers.map(s => s.nome).join(", ")}`);
      return;
    }
    if (confirm(`Excluir ${selectedIds.length} segmentos selecionados?`)) {
      for (const id of selectedIds) {
        await deleteMutation.mutateAsync(id);
      }
      setSelectedIds([]);
    }
  };

  const totalSuppliersToMove = segmentos?.filter(s => selectedIds.includes(s.id) && s.id !== mergeDestinoId).reduce((acc, curr) => acc + (curr.fornecedores?.length || 0), 0) || 0;

  return (
    <div className="p-4 md:p-8 space-y-6 pb-24 md:pb-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => fixSlugsMutation.mutate()}
            disabled={fixSlugsMutation.isPending}
            className="text-[10px] h-8"
          >
            <RefreshCw className={cn("w-3 h-3 mr-1", fixSlugsMutation.isPending && "animate-spin")} />
            Corrigir Slugs
          </Button>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input 
              placeholder="Buscar segmentos..." 
              className="pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {selectedIds.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  Ações em Lote ({selectedIds.length})
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => batchStatusMutation.mutate({ ids: selectedIds, ativo: true })}>
                  <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> Ativar Selecionados
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => batchStatusMutation.mutate({ ids: selectedIds, ativo: false })}>
                  <XCircle className="w-4 h-4 mr-2 text-gray-400" /> Desativar Selecionados
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setIsMergeModalOpen(true)}>
                  <Combine className="w-4 h-4 mr-2 text-blue-500" /> Mesclar Selecionados
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleBatchDelete} className="text-red-500">
                  <Trash2 className="w-4 h-4 mr-2" /> Excluir Selecionados
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <Button onClick={() => {
          setEditingSegmento(null);
          setFormData({ nome: "", slug: "", icone: "Package", ativo: true, ordem: 0 });
          setIsSlugAuto(true);
          setIsModalOpen(true);
        }}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Segmento
        </Button>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox 
                  checked={selectedIds.length === filteredSegmentos?.length && filteredSegmentos.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
              </TableHead>
              <TableHead>Ícone</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Fornecedores</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-10 text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10">Carregando...</TableCell></TableRow>
            ) : filteredSegmentos?.map((seg) => (
              <TableRow key={seg.id} className={cn(selectedIds.includes(seg.id) && "bg-muted/30")}>
                <TableCell>
                  <Checkbox 
                    checked={selectedIds.includes(seg.id)}
                    onCheckedChange={(checked) => {
                      if (checked) setSelectedIds(prev => [...prev, seg.id]);
                      else setSelectedIds(prev => prev.filter(id => id !== seg.id));
                    }}
                  />
                </TableCell>
                <TableCell>
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <IconComponent name={seg.icone} className="w-4 h-4" />
                  </div>
                </TableCell>
                <TableCell className="font-medium">
                  {seg.nome}
                  {seg.aliases && seg.aliases.length > 0 && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Apelidos: {seg.aliases.join(", ")}
                    </p>
                  )}
                </TableCell>
                <TableCell className="text-sm text-gray-500">{seg.slug}</TableCell>
                <TableCell>{seg.fornecedores?.length || 0}</TableCell>
                <TableCell>
                  {seg.ativo ? (
                    <Badge className="bg-green-100 text-green-700 shadow-none border-none">Ativo</Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-500 shadow-none border-none">Inativo</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(seg)}>
                        <Edit className="w-4 h-4 mr-2" /> Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => {
                        setSelectedIds([seg.id]);
                        setIsMergeModalOpen(true);
                      }}>
                        <Combine className="w-4 h-4 mr-2" /> Mesclar em outro
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleDelete(seg)} className="text-red-500">
                        <Trash2 className="w-4 h-4 mr-2" /> Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Modal Novo/Editar */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSegmento ? "Editar Segmento" : "Novo Segmento"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome do Segmento</Label>
              <Input 
                value={formData.nome} 
                onChange={(e) => {
                  const newNome = e.target.value;
                  setFormData(prev => {
                    const updates: any = { ...prev, nome: newNome };
                    if (isSlugAuto) {
                      updates.slug = generateSlug(newNome);
                    }
                    return updates;
                  });
                }}
                placeholder="Ex: Bijuterias"
              />
              {editingSegmento && (
                <p className="text-[11px] text-amber-600 font-medium">
                  {editingSegmento.fornecedores?.length || 0} fornecedores vinculados a este segmento.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Slug</Label>
                {!isSlugAuto && (
                  <button 
                    onClick={() => {
                      setIsSlugAuto(true);
                      setFormData(prev => ({ ...prev, slug: generateSlug(prev.nome) }));
                    }}
                    className="text-[10px] text-primary flex items-center gap-1 hover:underline"
                  >
                    <RefreshCw className="w-2.5 h-2.5" /> gerar de novo a partir do nome
                  </button>
                )}
              </div>
              <Input 
                value={formData.slug} 
                onChange={(e) => {
                  setIsSlugAuto(false);
                  setFormData(prev => ({ ...prev, slug: e.target.value }));
                }}
                placeholder="ex-bijuterias-e-acessorios"
                className={cn(!isSlugAuto && "border-amber-200 bg-amber-50/30")}
              />
            </div>

            <div className="space-y-2">
              <Label>Ícone</Label>
              <IconPicker 
                value={formData.icone} 
                onChange={(val) => setFormData(prev => ({ ...prev, icone: val }))} 
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Switch 
                checked={formData.ativo} 
                onCheckedChange={(val) => setFormData(prev => ({ ...prev, ativo: val }))}
              />
              <Label>Segmento Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMutation.mutate(formData)} disabled={saveMutation.isPending || !formData.nome || !formData.slug}>
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Mesclar */}
      <Dialog open={isMergeModalOpen} onOpenChange={setIsMergeModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mesclar Segmentos</DialogTitle>
            <DialogDescription>
              Isso moverá todos os fornecedores dos segmentos selecionados para um destino único e excluirá as origens.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg flex items-start gap-3 text-amber-800 text-sm">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold">Atenção!</p>
                <p>{totalSuppliersToMove} fornecedores serão movidos para o novo segmento.</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Segmentos de Origem</Label>
              <div className="flex flex-wrap gap-1.5">
                {segmentos?.filter(s => selectedIds.includes(s.id)).map(s => (
                  <Badge key={s.id} variant="secondary" className="gap-1">
                    {s.nome}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedIds(prev => prev.filter(id => id !== s.id))} />
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Segmento de Destino</Label>
              <Select value={mergeDestinoId} onValueChange={setMergeDestinoId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o segmento destino" />
                </SelectTrigger>
                <SelectContent>
                  {segmentos?.filter(s => !selectedIds.includes(s.id)).map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMergeModalOpen(false)}>Cancelar</Button>
            <Button 
              disabled={!mergeDestinoId || selectedIds.length === 0 || mergeMutation.isPending} 
              onClick={() => mergeMutation.mutate({ origemIds: selectedIds, destinoId: mergeDestinoId })}
            >
              Confirmar Mesclagem
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Minimal placeholder for missing components used in segments.tsx if needed
function Select({ value, onValueChange, children }: any) {
  return (
    <div className="relative">
      <select 
        value={value} 
        onChange={(e) => onValueChange(e.target.value)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none"
      >
        <option value="" disabled>Selecione...</option>
        {children}
      </select>
    </div>
  );
}

function SelectTrigger({ children, className }: any) {
  return <div className={cn("relative flex items-center justify-between", className)}>{children}</div>;
}

function SelectValue({ placeholder }: any) {
  return <span className="text-sm">{placeholder}</span>;
}

function SelectContent({ children }: any) {
  return <>{children}</>;
}

function SelectItem({ value, children }: any) {
  return <option value={value}>{children}</option>;
}
